import { Popover, Transition } from "@headlessui/react";
import { useAppSelector } from "hooks/hooks";
import { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import Avatar from "shared/Avatar/Avatar";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { socket } from "utils/socket";

interface NotifyDropdownProps {
  className?: string;
  open?: boolean;
  notifications: {
    notifications: Notification[];
    unreadCount: number;
    totalNotifications: number;
  };
  loadMoreNotifications: () => void;
  deleteAllNotifications: () => void;
  loading: boolean;
}

const NotifyDropdown: React.FC<NotifyDropdownProps> = ({
  className = "",
  notifications,
  loadMoreNotifications,
  deleteAllNotifications,
  loading,
}) => {
  const open = true;
  const getNotificationLink = (item: any): string => {
    const isUserAdmin = userState?.role === "admin";
    const locationLink = "/location/" + item.locationId;
    const prestationLink = "/prestations/" + item.prestationId;
    switch (item.operation) {
      case "create_reservation":
        return "/client/list-pending-reservations";
      case "confirm_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/confirmed" : `/client/messages`;
      case "cancel_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/canceled/client" : "/espace-evenementiel";
      case "negate_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/canceled/owner" : "/espace-evenementiel";
      case "confirm_owner_espace":
        return locationLink;
      case "negate_owner_espace":
        return "/client/locations";
      case "send_avis":
        return `${locationLink}#commentaires`;
      case "send_contat_request":
        return isUserAdmin ? "/admin/dashboard/reclamations" : "";
      case "create_espace":
        return isUserAdmin ? "/admin/dashboard/locations/unapproved" : "";
      case "create_prestation":
        return isUserAdmin ? "/admin/dashboard/prestations" : "";
      case "send_presta_avis":
        return `${prestationLink}#commentaires`;
      case "create_annonce":
        return isUserAdmin ? "/admin/dashboard/annonces/unapproved" : "";
      case "confirm_user_announce":
        return "/client/announces";
      case "block_user_announce":
        return "/client/announces";
      case "send_presta_devis":
        return "/prestataire/pricing";
      case "send_reservation_feedback":
        return isUserAdmin ? "/admin/dashboard/reservations-reclamations" : "/client/transferts-reservations";
      case "send_message":
        return "/client/messages";
      default:
        return "";
    }
  };

  const userState = useAppSelector((state) => state?.auth?.user);
  const user_avatar = "static-files/user_profile_2.png";
  const admin_avatar = "static-files/EVENT TO BEE-17.png";
  const getAvatarUrl = (item: any): string => {
    if (!item.senderId) {
      return process.env.REACT_APP_CLOUD_FRONT + user_avatar;
    }
    if (item?.sender === "ADMIN") {
      return process.env.REACT_APP_CLOUD_FRONT + admin_avatar;
    }
    const clientUrl = item?.senderData?.Clients[0]?.client_url?.trim();
    return clientUrl
      ? process.env.REACT_APP_CLOUD_FRONT + clientUrl
      : process.env.REACT_APP_CLOUD_FRONT + user_avatar;
  };

  const getSenderName = (item: any): string => {
    if (item?.sender !== "ADMIN") {
      return item?.sender;
    } else if (item?.sender === "ADMIN") {
      return "Eventtobee";
    } else {
      return item?.sender?.email || "Unknown ";
    }
  };
  const updateNotificationSeen = (item: any) => {
    if (userState) {
      socket.emit("update-notification", {
        userId: userState.id,
        notificationId: item.id,
      });
    }
  };

  useEffect(() => {}, []);
  useEffect(() => {}, [open]);

  return (
    <div className={className}>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`${
                open ? "bg-gray-100 dark:bg-neutral-800" : "text-opacity-90"
              } group p-3 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-full inline-flex items-center text-base font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-75 relative`}
            >
              {notifications.unreadCount >= 1 && (
                <span className="absolute flex items-center justify-center w-5 h-5 bg-blue-500 rounded-full top-1.5 right-1.5">
                  <span className="text-xs font-medium text-white">{notifications.unreadCount}</span>
                </span>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                className="absolute z-20 w-screen max-w-xs px-4 mt-3 sm:max-w-sm -right-4 sm:-right-8 sm:px-0"
                style={{
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <div className="overflow-hidden bg-white shadow-xl rounded-2xl ring-1 ring-black ring-opacity-5 dark:bg-neutral-800">
                  <div
                    id="header"
                    className="bg-white border-b border-gray-200 dark:bg-neutral-800 dark:border-neutral-700"
                  >
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            close();
                            deleteAllNotifications();
                          }}
                          className="text-sm font-medium text-blue-600 transition-colors duration-200 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          Tout Effacer
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative grid gap-4 p-4 bg-white dark:bg-neutral-800">
                    {notifications.notifications &&
                      notifications.notifications.map((item: any, index: any) => {
                        return (
                          item && (
                            <Link
                              key={index}
                              to={getNotificationLink(item)}
                              className={`${
                                item?.vue === false
                                  ? "bg-blue-50 dark:bg-blue-900/50"
                                  : "bg-white dark:bg-neutral-800"
                              } flex p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition duration-200 ease-in-out `}
                              onClick={() => {
                                close();
                                updateNotificationSeen(item);
                              }}
                            >
                              <Avatar
                                sizeClass="w-10 h-10 sm:w-12 sm:h-12"
                                imgUrl={getAvatarUrl(item)}
                                className="flex-shrink-0"
                              />
                              <div className="flex-1 ml-3 space-y-1 sm:ml-4">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                                  {getSenderName(item)}
                                </p>
                                <p className="text-xs text-gray-600 sm:text-sm dark:text-gray-300 line-clamp-2">
                                  {item.message}
                                </p>
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                  <p>{new Date(item.createdAt).toISOString().split("T")[0]}</p>
                                  <p>
                                    {new Date(item.createdAt).toLocaleTimeString("fr-FR", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )
                        );
                      })}
                    {notifications.notifications.length < notifications.totalNotifications && (
                      <ButtonSecondary
                        loading={loading}
                        onClick={loadMoreNotifications}
                        className="w-full py-2 mt-4 text-sm font-medium text-blue-600 transition-colors duration-200 rounded-lg dark:text-blue-400 bg-blue-50 dark:bg-blue-900/50 hover:bg-blue-100 dark:hover:bg-blue-900"
                      >
                        Voir plus
                      </ButtonSecondary>
                    )}
                    {notifications.totalNotifications === 0 && (
                      <h3 className="py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                        Actuellement, il n'y a aucune notification en cours.
                      </h3>
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default NotifyDropdown;
